<template>
  <div>
    <div
      v-if="dataLoading"
      class="fixed inset-0 flex items-center justify-center bg-surface-900 bg-opacity-50 z-50"
    >
      <div class="bg-surface-0 dark:bg-surface-800 p-4 rounded-lg shadow-lg flex flex-col items-center">
        <i class="pi pi-spin pi-spinner text-4xl text-primary mb-2" />
        <span class="text-lg">Loading application data...</span>
      </div>
    </div>
    <app-dashboard>
      <!-- Main content slot -->
      <slot />

      <!-- Add drawer content slot -->
      <template #drawer-content="{ data }">
        <slot
          name="drawer-content"
          :data="data"
        />
      </template>
    </app-dashboard>

    <!-- Add SubscriptionDebugPanel -->
    <DevSubscriptionDebugPanel />

    <!-- Chatbot Widget -->
    <ChatbotWidget />

    <!-- Membership dues reminder dialog -->
    <Dialog
      v-model:visible="showDuesReminderDialog"
      modal
      header="Membership Dues Required"
      :style="{ width: '90%', maxWidth: '560px' }"
      :closable="true"
      :draggable="false"
    >
      <div class="p-4 space-y-6">
        <!-- Info icon at very top -->
        <div class="flex justify-center">
          <i class="pi pi-info-circle text-blue-500 text-4xl" />
        </div>

        <!-- Sponsored fast path message (no box) -->
        <template v-if="showSponsoredFastPath">
          <div class="text-center space-y-4">
            <p class="text-sm leading-relaxed">
              <span v-if="sponsoredPreapprovedSubkrewes.length === 1">
                You have been pre-approved for the sponsored SubKrewe
                <strong>"{{ sponsoredPreapprovedSubkrewes[0].name }}"</strong>. Join now to skip paying membership dues.
              </span>
              <span v-else>
                You have been pre-approved for multiple sponsored SubKrewes.
                Select one below and join now to skip paying membership dues.
              </span>
            </p>
            <!-- Single option button -->
            <div v-if="sponsoredPreapprovedSubkrewes.length === 1">
              <Button
                :disabled="joinInProgress"
                size="small"
                :label="joinInProgress ? 'Joining…' : `Join ${sponsoredPreapprovedSubkrewes[0].name} Now`"
                icon="pi pi-user-plus"
                @click="joinSelectedSponsored()"
              />
            </div>
            <!-- Multiple selection -->
            <div
              v-else
              class="flex flex-col items-center gap-3"
            >
              <Dropdown
                v-model="selectedSponsoredSubkreweId"
                :options="sponsoredPreapprovedSubkrewes"
                option-label="name"
                option-value="id"
                placeholder="Select a sponsored SubKrewe"
                class="w-full"
              />
              <div class="flex gap-2">
                <Button
                  :disabled="!selectedSponsoredSubkreweId || joinInProgress"
                  size="small"
                  :label="joinInProgress ? 'Joining…' : 'Join Selected Now'"
                  icon="pi pi-user-plus"
                  @click="joinSelectedSponsored()"
                />
                <Button
                  v-if="selectedSponsoredSubkreweId"
                  size="small"
                  label="View Details"
                  icon="pi pi-external-link"
                  class="p-button-text"
                  @click="navigateToSubkrewe(selectedSponsoredSubkreweId)"
                />
              </div>
            </div>
          </div>

          <!-- Separator -->
          <div class="relative">
            <div class="flex items-center">
              <div class="flex-grow border-t border-gray-300" />
              <div class="mx-4 text-gray-500">
                Or
              </div>
              <div class="flex-grow border-t border-gray-300" />
            </div>
          </div>
        </template>

        <!-- Band fast path message -->
        <template v-if="showBandFastPath">
          <div class="text-center space-y-4">
            <p class="text-sm leading-relaxed">
              <span v-if="bandPreapprovedSubkrewes.length === 1">
                You have been pre-approved for the band
                <strong>"{{ bandPreapprovedSubkrewes[0].name }}"</strong>. Join now to skip paying membership dues.
              </span>
              <span v-else>
                You have been pre-approved for multiple bands.
                Select one below and join now to skip paying membership dues.
              </span>
            </p>
            <!-- Single option button -->
            <div v-if="bandPreapprovedSubkrewes.length === 1">
              <Button
                :disabled="joinBandInProgress"
                size="small"
                :label="joinBandInProgress ? 'Joining…' : `Join ${bandPreapprovedSubkrewes[0].name} Now`"
                icon="pi pi-user-plus"
                @click="joinSelectedBand()"
              />
            </div>
            <!-- Multiple selection -->
            <div
              v-else
              class="flex flex-col items-center gap-3"
            >
              <Dropdown
                v-model="selectedBandSubkreweId"
                :options="bandPreapprovedSubkrewes"
                option-label="name"
                option-value="id"
                placeholder="Select a band"
                class="w-full"
              />
              <div class="flex gap-2">
                <Button
                  :disabled="!selectedBandSubkreweId || joinBandInProgress"
                  size="small"
                  :label="joinBandInProgress ? 'Joining…' : 'Join Selected Now'"
                  icon="pi pi-user-plus"
                  @click="joinSelectedBand()"
                />
                <Button
                  v-if="selectedBandSubkreweId"
                  size="small"
                  label="View Details"
                  icon="pi pi-external-link"
                  class="p-button-text"
                  @click="navigateToSubkrewe(selectedBandSubkreweId)"
                />
              </div>
            </div>
          </div>

          <!-- Separator if there are both band and sponsored OR if showing standard dues -->
          <div
            v-if="showSponsoredFastPath"
            class="relative"
          >
            <div class="flex items-center">
              <div class="flex-grow border-t border-gray-300" />
              <div class="mx-4 text-gray-500">
                Or
              </div>
              <div class="flex-grow border-t border-gray-300" />
            </div>
          </div>
        </template>

        <!-- Standard dues info -->
        <div class="space-y-4">
          <p class="text-center">
            You need to pay your membership dues before joining a SubKrewe. Pay now to unlock all member benefits.
          </p>
          <p class="text-center">
            If you received a code from your SubKrewe captain, click Redeem Code to apply it instead of paying. You will be automatically added to the SubKrewe you received the code from.
          </p>
          <p class="text-center">
            Payments will be handled by Eventbrite. You will receive an order confirmation email from them after your payment is processed, but you will <b>NOT</b> receive printable tickets. At wristband pickup, you will need to show your Krewebacchus QR code to receive your wristband. (Note: QR codes are not available yet, but will be before pickup)
          </p>
          <p class="text-center text-sm text-gray-500">
            Note: Memberships for the Intergalactic Krewe of Chewbacchus are non-refundable.
          </p>
        </div>

        <!-- Action buttons -->
        <div class="flex justify-center gap-2 flex-nowrap">
          <Button
            label="Maybe Later"
            icon="pi pi-times"
            class="p-button-text p-button-danger p-button-sm whitespace-nowrap"
            @click="showDuesReminderDialog = false"
          />
          <SharedPurchaseButton
            ticket-type="membership"
            class="p-button-primary"
            :label="'Pay Dues'"
            button-size="small"
            @checkout-closed="showDuesReminderDialog = false"
          />
          <SharedRedeemCodeLauncher
            button-size="small"
            class="whitespace-nowrap"
            @redeemed="showDuesReminderDialog = false"
          />
        </div>
      </div>
    </Dialog>
    <!-- Children tickets reminder dialog -->
    <Dialog
      v-model:visible="showChildrenTicketsDialog"
      modal
      header="Children Tickets Needed"
      :style="{ width: '90%', maxWidth: '500px' }"
      :closable="true"
      :draggable="false"
    >
      <div class="p-4 space-y-6">
        <!-- Warning icon -->
        <div class="flex justify-center">
          <i class="pi pi-exclamation-triangle text-amber-500 text-4xl" />
        </div>

        <!-- Explanation -->
        <div class="space-y-4">
          <p class="text-center">
            You have <strong>{{ childrenWithoutTickets.length }}</strong>
            {{ childrenWithoutTickets.length === 1 ? 'child' : 'children' }}
            registered who {{ childrenWithoutTickets.length === 1 ? 'doesn\'t' : 'don\'t' }} have parade tickets yet.
          </p>
          <p class="text-center">
            Purchase tickets now so they can ride with you on parade day!
          </p>
          <p class="text-center text-sm text-gray-500">
            Children's tickets are separate from membership dues.
          </p>
        </div>

        <!-- Action buttons -->
        <div class="flex justify-center gap-2 flex-nowrap">
          <Button
            label="Maybe Later"
            icon="pi pi-times"
            class="p-button-text p-button-secondary p-button-sm whitespace-nowrap"
            @click="showChildrenTicketsDialog = false"
          />
          <SharedPurchaseButton
            ticket-type="children"
            button-severity="warning"
            button-label="Buy Children Tickets"
            icon="pi pi-ticket"
            button-size="small"
            :children-ids="childrenWithoutTickets"
            @checkout-closed="showChildrenTicketsDialog = false"
          />
        </div>
      </div>
    </Dialog>

    <!-- Find SubKrewe reminder dialog (shown when member has paid but no subkrewe) -->
    <Dialog
      v-model:visible="showFindSubkreweDialog"
      modal
      header="Find Your SubKrewe!"
      :style="{ width: '90%', maxWidth: '500px' }"
      :closable="true"
      :draggable="false"
      @hide="findSubkreweDialogDismissed = true"
    >
      <div class="p-4 space-y-6">
        <!-- Success icon -->
        <div class="flex justify-center">
          <i class="pi pi-check-circle text-green-500 text-4xl" />
        </div>

        <!-- Explanation -->
        <div class="space-y-4">
          <p class="text-center text-lg font-semibold text-green-700 dark:text-green-400">
            Your membership dues are paid!
          </p>
          <p class="text-center">
            Now it's time to find a SubKrewe to join. SubKrewes are the smaller groups within the Krewe that you'll parade with.
          </p>
          <p class="text-center text-sm text-gray-500">
            Browse active SubKrewes and find one that matches your interests!
          </p>
        </div>

        <!-- Action buttons -->
        <div class="flex justify-center gap-2 flex-nowrap">
          <Button
            label="Maybe Later"
            icon="pi pi-times"
            class="p-button-text p-button-secondary p-button-sm whitespace-nowrap"
            @click="showFindSubkreweDialog = false; findSubkreweDialogDismissed = true"
          />
          <Button
            label="Find SubKrewe"
            icon="pi pi-search"
            class="p-button-primary p-button-sm"
            @click="navigateToFindSubkrewe"
          />
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import { useCurrentUser } from 'vuefire'
import { useMemberPreapprovals } from '~/composables/useMemberPreapprovals.js'
import { useChildrenTickets } from '~/composables/useChildrenTickets.js'

const { loadAllData, loading: dataLoading } = useStorePreloader()
const route = useRoute()
const router = useRouter()
const toast = useToast()

// Load all data when the app starts
onMounted(() => {
  loadAllData()
  setupDuesDialogLogic()
})

// Dialog state
const showDuesReminderDialog = ref(false)
const showChildrenTicketsDialog = ref(false)
const showFindSubkreweDialog = ref(false)
const findSubkreweDialogDismissed = ref(false)

// Use the centralized shouldPayDues composable
const shouldPayDues = useShouldPayDues()

// Use children tickets composable
const { childrenWithoutTickets, hasChildrenWithoutTickets } = useChildrenTickets()

// Pre-approved sponsored SubKrewe fast path (centralized)
const { load: loadPreapprovals, sponsoredPreapprovedSubkrewes, bandPreapprovedSubkrewes } = useMemberPreapprovals()
const subkrewesStore = useSubKrewesStore()
const membersStore = useMembersStore()
const tasksStore = useTasksStore()
const currentUser = useCurrentUser()
const selectedSponsoredSubkreweId = ref<string | null>(null)
const selectedBandSubkreweId = ref<string | null>(null)
const joinInProgress = ref(false)
const joinBandInProgress = ref(false)
const memberPath = computed(() => currentUser.value?.uid ? `members/${currentUser.value.uid}` : null)
const memberData = computed<Member | null>(() => memberPath.value ? (membersStore.members[memberPath.value] || null) : null)
const hasPendingJoinRequest = computed(() => {
  return tasksStore.tasksArray.some((task) => {
    if (task.taskImplementation !== 'SubKreweJoinRequestTask') return false
    if (task.metadata?.memberId !== memberData.value?.id) return false
    if (task.status !== 'pending' && task.status !== 'in_progress') return false
    return true
  })
})
const hasPaidButNoSubkrewe = computed(() => {
  // User has paid (shouldPayDues is false) but has no subkrewe
  // Don't show if they have a pending join request
  if (hasPendingJoinRequest.value) return false
  return !shouldPayDues.value && memberData.value && !memberData.value.subkrewe?.id
})
const showSponsoredFastPath = computed(() => {
  const list = sponsoredPreapprovedSubkrewes?.value
  return shouldPayDues.value && Array.isArray(list) && list.length > 0
})
const showBandFastPath = computed(() => {
  const list = bandPreapprovedSubkrewes?.value
  return shouldPayDues.value && Array.isArray(list) && list.length > 0
})

// (Removed unused sponsoredFastPathText after design change)

watch(sponsoredPreapprovedSubkrewes, (list) => {
  if (Array.isArray(list) && list.length === 1) selectedSponsoredSubkreweId.value = list[0].id
}, { immediate: true })

watch(bandPreapprovedSubkrewes, (list) => {
  if (Array.isArray(list) && list.length === 1) selectedBandSubkreweId.value = list[0].id
}, { immediate: true })

async function joinSelectedSponsored() {
  const targetId = selectedSponsoredSubkreweId.value
  if (!targetId) return
  // Pinia getter getSubkreweById returns a function; when accessed via store it is already the inner function
  const subGetter = subkrewesStore.getSubkreweById as unknown as (id: string) => SubKrewe | undefined
  const sub = subGetter ? subGetter(targetId) : undefined
  if (!sub || !memberData.value) return
  try {
    joinInProgress.value = true
    const ok = await subkrewesStore.addMemberToSubKrewe(sub, memberData.value as Member)
    if (ok) {
      toast.add({ severity: 'success', summary: 'Joined', detail: `You joined ${sub.name}.`, life: 3000 })
      showDuesReminderDialog.value = false
    }
    else {
      toast.add({ severity: 'error', summary: 'Failed', detail: 'Could not join SubKrewe.', life: 3000 })
    }
  }
  catch (e) {
    console.error('[default] join sponsored error', e)
    toast.add({ severity: 'error', summary: 'Error', detail: 'Unexpected error joining.', life: 3000 })
  }
  finally {
    joinInProgress.value = false
  }
}

async function joinSelectedBand() {
  const targetId = selectedBandSubkreweId.value
  if (!targetId) return
  const subGetter = subkrewesStore.getSubkreweById as unknown as (id: string) => SubKrewe | undefined
  const sub = subGetter ? subGetter(targetId) : undefined
  if (!sub || !memberData.value) return
  try {
    joinBandInProgress.value = true
    const ok = await subkrewesStore.addMemberToSubKrewe(sub, memberData.value as Member)
    if (ok) {
      toast.add({ severity: 'success', summary: 'Joined', detail: `You joined ${sub.name}.`, life: 3000 })
      showDuesReminderDialog.value = false
    }
    else {
      toast.add({ severity: 'error', summary: 'Failed', detail: 'Could not join band.', life: 3000 })
    }
  }
  catch (e) {
    console.error('[default] join band error', e)
    toast.add({ severity: 'error', summary: 'Error', detail: 'Unexpected error joining.', life: 3000 })
  }
  finally {
    joinBandInProgress.value = false
  }
}

function navigateToSubkrewe(_id: string) {}

function navigateToFindSubkrewe() {
  showFindSubkreweDialog.value = false
  findSubkreweDialogDismissed.value = true
  router.push('/subkrewe')
}

// Setup dialog logic and watch for route changes
const setupDuesDialogLogic = () => {
  // Watch for route changes and shouldPayDues changes to show/hide the dues dialog
  watch(
    () => [route.path, shouldPayDues.value],
    ([_path, shouldPay]) => {
      // If user has paid, close the dialog
      if (!shouldPay) {
        showDuesReminderDialog.value = false
        return
      }

      showDuesReminderDialog.value = true
      // Lazy load preapproved sponsored and band options when dialog appears, ensuring subkrewes loaded
      ;(async () => {
        try {
          const subStore = useSubKrewesStore()
          await subStore.ensureSubKrewesLoaded?.()
          await loadPreapprovals()
          const subGetter = subStore.getSubkreweById as unknown as (id: string) => SubKrewe | undefined

          // Fetch sponsored subkrewes
          for (const p of sponsoredPreapprovedSubkrewes.value) {
            if (!subGetter?.(p.id)) {
              try {
                await subStore.fetchSubKrewe(p.id)
              }
              catch {
                /* noop */
              }
            }
          }
          if (sponsoredPreapprovedSubkrewes.value.length === 1) {
            selectedSponsoredSubkreweId.value = sponsoredPreapprovedSubkrewes.value[0].id
          }

          // Fetch band subkrewes
          for (const p of bandPreapprovedSubkrewes.value) {
            if (!subGetter?.(p.id)) {
              try {
                await subStore.fetchSubKrewe(p.id)
              }
              catch {
                /* noop */
              }
            }
          }
          if (bandPreapprovedSubkrewes.value.length === 1) {
            selectedBandSubkreweId.value = bandPreapprovedSubkrewes.value[0].id
          }
        }
        catch (e) {
          console.error('[default] fast-path preload error', e)
        }
      })()
    },
    { immediate: true },
  )

  // Watch for children tickets dialog - show after dues are paid but children tickets aren't
  // Include route.path to re-show on navigation (like dues dialog does for unpaid dues)
  watch(
    () => [route.path, shouldPayDues.value, hasChildrenWithoutTickets.value],
    ([_path, shouldPay, hasUnpaidChildren]) => {
      // Only show if: dues paid and has unpaid children
      if (!shouldPay && hasUnpaidChildren) {
        showChildrenTicketsDialog.value = true
      }
    },
    { immediate: true },
  )

  // Watch for find subkrewe dialog - show when member has paid but no subkrewe
  // Only show once per session (until dismissed)
  watch(
    () => hasPaidButNoSubkrewe.value,
    (shouldShow) => {
      // Only show if not already dismissed this session and condition is met
      if (shouldShow && !findSubkreweDialogDismissed.value && !showDuesReminderDialog.value) {
        showFindSubkreweDialog.value = true
      }
      else if (!shouldShow) {
        // Member now has a subkrewe, close the dialog
        showFindSubkreweDialog.value = false
      }
    },
    { immediate: true },
  )
}
</script>

<style>

</style>
